import Head from 'next/head'
import React from 'react'
import { Thing, WithContext } from 'schema-dts'
import { filterFalsy } from 'src/utils/array'
import { BASE_PATH } from 'src/utils/routes'

type MetaItem = {
  name: string
  content: string
}

export type SEOProps = {
  path: string
  basePath?: string
  image: string
  description: string
  pageTitle?: string
  siteTitle: string
  siteUrl: string
  author?: string
  meta?: MetaItem[]
  structuredData?: WithContext<Thing>
  noindex?: boolean
}

export const SEO: React.FC<SEOProps> = (props) => {
  const {
    path,
    basePath = BASE_PATH,
    pageTitle,
    description,
    image,
    siteTitle,
    siteUrl,
    author,
    structuredData,
    meta = [],
    noindex = false,
  } = props

  const pageUrl = filterFalsy([siteUrl, basePath, path]).join('')
  const title = pageTitle ? `${pageTitle} | ${siteTitle}` : siteTitle
  const siteDescription = description
  const siteAuthor = author
  const siteImage = image.startsWith('https') ? image : `${siteUrl}/${image}`
  const metaData = [
    {
      name: 'description',
      content: siteDescription,
    },
    {
      name: 'image',
      content: siteImage,
    },
    {
      property: 'og:url',
      content: pageUrl,
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:title',
      content: title,
    },
    {
      property: 'og:description',
      content: siteDescription,
    },
    {
      property: 'og:image',
      content: siteImage,
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:creator',
      content: siteAuthor,
    },
    {
      name: 'twitter:title',
      content: title,
    },
    {
      name: 'twitter:description',
      content: siteDescription,
    },
    {
      name: 'twitter:image',
      content: siteImage,
    },
  ]
    .concat(meta)
    .filter(({ content }) => !!content)
  if (noindex) metaData.push({ name: 'robots', content: 'noindex' })

  const linkData = [
    {
      rel: 'canonical',
      href: pageUrl,
    },
  ]
  return (
    <Head>
      <title>{title}</title>
      {metaData.map((item) => (
        <meta key={item.name || item.property} {...item} />
      ))}
      {linkData.map((item) => (
        <link key={item.href} {...item} />
      ))}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Head>
  )
}
